
/**
 * Address cache with LRU eviction and change detection.
 * 
 * Manages caching of standardized addresses with sophisticated strategies including:
 * - LRU (Least Recently Used) eviction policy
 * - Time-based expiration of cache entries
 * - Change detection for address components (logradouro, bairro, municipio)
 * - Callback-based notifications for address changes
 * - Observer pattern integration
 * 
 * Implements singleton pattern ensuring only one cache instance exists.
 * 
 * **REFACTORED v0.9.0-alpha**: Now uses composition with AddressChangeDetector,
 * CallbackRegistry, and AddressDataStore for improved maintainability.
 * 
 * @module data/AddressCache
 * @since 0.9.0-alpha
 * @author Marcelo Pereira Barbosa
 */

import ObserverSubject from '../core/ObserverSubject.js';
import { withObserver } from '../utils/ObserverMixin.js';
import AddressExtractor from './AddressExtractor.js';
import LRUCache from './LRUCache.js';
import { log } from '../utils/logger.js';
import timerManager from '../utils/TimerManager.js';
import type { NominatimResponse } from '../types/nominatim.js';
import type BrazilianStandardAddress from './BrazilianStandardAddress.js';

// NEW: Import refactored classes (v0.9.0-alpha)
import AddressChangeDetector from './AddressChangeDetector.js';
import { CallbackRegistry } from 'bessa_patterns.ts';
import AddressDataStore from './AddressDataStore.js';

// NEW (v0.12.12-alpha): Confirmation buffers for GPS intersection jitter mitigation
import AddressFieldConfirmationBuffer from './AddressFieldConfirmationBuffer.js';
import LogradouroChangeTrigger from './LogradouroChangeTrigger.js';
import type {
	AddressConfirmationThresholdOptions,
	AddressConfirmationThresholds
} from '../config/addressConfirmation.js';
import {
	resolveAddressConfirmationThresholds,
	DEFAULT_ADDRESS_CONFIRMATION_THRESHOLDS
} from '../config/addressConfirmation.js';

export interface FieldBufferState {
	confirmed:    string | null;
	isConfirmed:  boolean;
	pending:      string | null;
	hasPending:   boolean;
	pendingCount: number;
	threshold:    number;
}

export interface ConfirmationBufferState {
	logradouro: FieldBufferState;
	bairro:     FieldBufferState;
	municipio:  FieldBufferState;
}

class AddressCache {
	static confirmationThresholds: Readonly<AddressConfirmationThresholds> =
		DEFAULT_ADDRESS_CONFIRMATION_THRESHOLDS;

	/**
	 * Singleton instance holder. Only one AddressCache exists per application.
	 * @static
	 * @type {AddressCache|null}
	 * @private
	 */
	static instance: AddressCache | null = null;

	/**
	 * Gets or creates the singleton AddressCache instance.
	 * 
	 * Implements the singleton pattern ensuring only one AddressCache instance
	 * exists throughout the application lifecycle.
	 *
	 * Pass `addressConfirmationBufferThreshold` to override the default
	 * confirmation buffer size for all tracked address fields. Invalid overrides
	 * are ignored so the existing 3-reading behavior remains unchanged.
	 * 
	 * @static
	 * @returns {AddressCache} The singleton AddressCache instance
	 * @since 0.9.0-alpha
	 */
	static getInstance(options?: AddressConfirmationThresholdOptions): AddressCache {
		if (options) {
			AddressCache.configure(options);
		}
		if (!AddressCache.instance) {
			AddressCache.instance = new AddressCache(AddressCache.confirmationThresholds);
		}
		return AddressCache.instance!;
	}

	/**
	 * Updates the confirmation-buffer configuration for the singleton.
	 *
	 * Reconfiguration swaps in new buffer instances and clears any pending
	 * candidates so old state is not interpreted using a new threshold.
	 */
	static configure(options: AddressConfirmationThresholdOptions = {}): Readonly<AddressConfirmationThresholds> {
		const thresholds = resolveAddressConfirmationThresholds(options);
		AddressCache.confirmationThresholds = thresholds;
		if (AddressCache.instance) {
			AddressCache.instance.applyConfirmationThresholds(thresholds);
		}
		return thresholds;
	}

	/**
	 * Creates a new AddressCache instance.
	 * 
	 * Initializes the cache with default settings and empty state.
	 * This constructor is typically called internally by the getInstance() method
	 * to maintain the singleton pattern.
	 * 
	 * **REFACTORED v0.9.0-alpha**: Now uses composition with three focused classes:
	 * - AddressChangeDetector for change detection logic
	 * - CallbackRegistry for callback management
	 * - AddressDataStore for data storage
	 * 
	 * @private
	 * @since 0.9.0-alpha
	 */
	observerSubject!: ObserverSubject;
	declare subscribe: (observer: unknown) => void;
	declare unsubscribe: (observer: unknown) => void;
	declare notifyObservers: (...args: unknown[]) => void;
	cache: LRUCache;
	changeDetector: AddressChangeDetector;
	callbackRegistry: CallbackRegistry;
	dataStore: AddressDataStore;
	lastNotifiedChangeSignature: string | null;
	lastNotifiedBairroChangeSignature: string | null;
	lastNotifiedMunicipioChangeSignature: string | null;
	logradouroChangeCallback: ((details: unknown) => void) | null;
	bairroChangeCallback: ((details: unknown) => void) | null;
	municipioChangeCallback: ((details: unknown) => void) | null;
	currentAddress: BrazilianStandardAddress | null;
	previousAddress: BrazilianStandardAddress | null;
	currentRawData: NominatimResponse | null;
	previousRawData: NominatimResponse | null;
	// NEW (v0.12.12-alpha): Confirmation buffers — require N consecutive identical
	// geocoding results before publishing an address-field change (jitter mitigation).
	// _logradouroTrigger is a dedicated module that owns all announcement-decision
	// logic for logradouro changes (including the bypassDistance signal).
	private _logradouroTrigger: LogradouroChangeTrigger;
	private _bairroBuffer: AddressFieldConfirmationBuffer;
	private _municipioBuffer: AddressFieldConfirmationBuffer;
	// Callback fired when any buffer enters/exits pending state (used to switch
	// GeolocationService to fast-throttle mode during a confirmation window)
	private _pendingConfirmationCallback: ((isPending: boolean) => void) | null;
	private _anyBufferPending: boolean;
	private _confirmationThresholds: Readonly<AddressConfirmationThresholds>;

	/**
	 * @param confirmationThresholds - Normalized thresholds for the three
	 *   address-field confirmation buffers.
	 */
	constructor(
		confirmationThresholds: Readonly<AddressConfirmationThresholds> = DEFAULT_ADDRESS_CONFIRMATION_THRESHOLDS
	) {
		this.observerSubject = new ObserverSubject();
		
		// Use LRUCache for efficient caching with automatic eviction
		this.cache = new LRUCache(50, 300000); // 50 entries, 5 minutes expiration
		
		// NEW (v0.9.0-alpha): Composition with focused classes
		this.changeDetector = new AddressChangeDetector();
		this.callbackRegistry = new CallbackRegistry();
		this.dataStore = new AddressDataStore();
		
		// DEPRECATED (v0.9.0-alpha): Legacy properties kept for backward compatibility
		// These are now managed by the composed classes above but maintained
		// for any direct property access (use getInstance() methods instead)
		// Initialize from dataStore to ensure consistency
		this.lastNotifiedChangeSignature = null;
		this.lastNotifiedBairroChangeSignature = null;
		this.lastNotifiedMunicipioChangeSignature = null;
		this.logradouroChangeCallback = null;
		this.bairroChangeCallback = null;
		this.municipioChangeCallback = null;
		this.currentAddress = this.dataStore.getCurrent().address;
		this.previousAddress = this.dataStore.getPrevious().address;
		this.currentRawData = this.dataStore.getCurrentRawData();
		this.previousRawData = this.dataStore.getPreviousRawData();

		// NEW (v0.12.12-alpha): one confirmation buffer per tracked address field
		this._confirmationThresholds = confirmationThresholds;
		this._logradouroTrigger = new LogradouroChangeTrigger(confirmationThresholds.logradouro);
		this._bairroBuffer     = new AddressFieldConfirmationBuffer(confirmationThresholds.bairro);
		this._municipioBuffer  = new AddressFieldConfirmationBuffer(confirmationThresholds.municipio);
		this._pendingConfirmationCallback = null;
		this._anyBufferPending = false;
		
		// Instance-based cleanup timer using TimerManager (prevents memory leaks)
		timerManager.setInterval(() => {
			this.cleanExpiredEntries();
		}, 60000, 'address-cache-cleanup'); // Clean expired entries every 60 seconds
	}

	private applyConfirmationThresholds(
		confirmationThresholds: Readonly<AddressConfirmationThresholds>
	): void {
		const thresholdsChanged =
			this._confirmationThresholds.logradouro !== confirmationThresholds.logradouro ||
			this._confirmationThresholds.bairro !== confirmationThresholds.bairro ||
			this._confirmationThresholds.municipio !== confirmationThresholds.municipio;

		if (!thresholdsChanged) {
			return;
		}

		const hadPendingBuffers = this._anyBufferPending;

		this._confirmationThresholds = confirmationThresholds;
		this._logradouroTrigger = new LogradouroChangeTrigger(confirmationThresholds.logradouro);
		this._bairroBuffer = new AddressFieldConfirmationBuffer(confirmationThresholds.bairro);
		this._municipioBuffer = new AddressFieldConfirmationBuffer(confirmationThresholds.municipio);
		this._anyBufferPending = false;

		if (hadPendingBuffers && this._pendingConfirmationCallback) {
			this._pendingConfirmationCallback(false);
		}
	}

	/**
	 * Generates a cache key for address data to enable efficient caching and retrieval.
	 * 
	 * **REFACTORED v0.9.0-alpha**: Delegates to AddressDataStore.generateCacheKey()
	 * 
	 * @param {Object} data - Address data from geocoding API
	 * @returns {string|null} Cache key string or null if data is invalid
	 * @since 0.9.0-alpha
	 * @author Marcelo Pereira Barbosa
	 */
	generateCacheKey(data: unknown) {
		return AddressDataStore.generateCacheKey(data as NominatimResponse | null);
	}

	/**
	 * Static wrapper for backward compatibility.
	 * @deprecated Use getInstance().generateCacheKey() or AddressDataStore.generateCacheKey() instead
	 * @static
	 */
	static generateCacheKey(data: unknown) {
		return AddressDataStore.generateCacheKey(data as NominatimResponse | null);
	}

	/**
	 * Evicts least recently used cache entries when maximum cache size is reached.
	 * 
	 * Delegates to LRUCache which automatically handles LRU eviction when
	 * entries are added via set(). This method is now a no-op since eviction
	 * happens automatically, but kept for backward compatibility.
	 * 
	 * @private
	 * @since 0.9.0-alpha
	 * @deprecated Eviction now automatic in LRUCache.set()
	 */
	evictLeastRecentlyUsedIfNeeded() {
		// No-op: LRUCache handles eviction automatically in set()
		// Kept for backward compatibility
	}

	/**
	 * Cleans up expired cache entries based on timestamp.
	 * 
	 * Delegates to LRUCache.cleanExpired() which removes all entries
	 * that have exceeded the expiration time.
	 * 
	 * @private
	 * @since 0.9.0-alpha
	 */
	cleanExpiredEntries() {
		const removed = this.cache.cleanExpired();
		
		if (removed > 0) {
			log(`(AddressCache) Cleaned ${removed} expired cache entries`);
		}
	}

	/**
	 * Static wrapper for backward compatibility.
	 * @deprecated Use getInstance().cleanExpiredEntries() instead
	 * @static
	 */
	static cleanExpiredEntries() {
		return AddressCache.getInstance().cleanExpiredEntries();
	}

	/**
	 * Clears all cache entries and resets change tracking.
	 * This method is primarily used for testing purposes.
	 * 
	 * @since 0.9.0-alpha
	 */
	clearCache() {
		this.cache.clear();
		this.currentAddress = null;
		this.previousAddress = null;
		this.currentRawData = null;
		this.previousRawData = null;
		this.lastNotifiedChangeSignature = null;
		this.lastNotifiedBairroChangeSignature = null;
		this.lastNotifiedMunicipioChangeSignature = null;
		
		// Clear changeDetector signatures for fresh start
		this.changeDetector.clearAllSignatures();
		
		// Clear dataStore history
		this.dataStore.clear();

		// Reset confirmation buffers so tests start with a clean state
		this._logradouroTrigger.reset();
		this._bairroBuffer.reset();
		this._municipioBuffer.reset();
	}

	/**
	 * Static wrapper for backward compatibility.
	 * @deprecated Use getInstance().clearCache() instead
	 * @static
	 */
	static clearCache() {
		return AddressCache.getInstance().clearCache();
	}

	/**
	 * Sets the callback function to be called when logradouro changes are detected.
	 * 
	 * This method allows external components to register a callback function that will be
	 * invoked whenever a street (logradouro) change is detected between address updates.
	 * 
	 * @param {Function|null} callback - Function to call on logradouro changes, or null to remove callback
	 * @param {Object} callback.changeDetails - Details about the logradouro change
	 * @returns {void}
	 * 
	 * **REFACTORED v0.9.0-alpha**: Delegates to CallbackRegistry
	 * 
	 * @example
	 * const cache = AddressCache.getInstance();
	 * cache.setLogradouroChangeCallback((changeDetails) => {
	 *   log('Street changed:', changeDetails);
	 * });
	 * 
	 * @since 0.9.0-alpha
	 * @author Marcelo Pereira Barbosa
	 */
	setLogradouroChangeCallback(callback: ((details: unknown) => void) | null) {
		this.callbackRegistry.register('logradouro', callback);
		// Legacy: Keep property synchronized for backward compatibility
		this.logradouroChangeCallback = callback;
	}

	/**
	 * Static wrapper for backward compatibility.
	 * @deprecated Use getInstance().setLogradouroChangeCallback() instead
	 * @static
	 */
	static setLogradouroChangeCallback(callback: ((details: unknown) => void) | null) {
		return AddressCache.getInstance().setLogradouroChangeCallback(callback);
	}

	/**
	 * Sets the callback function to be called when bairro changes are detected.
	 * 
	 * This method allows external components to register a callback function that will be
	 * invoked whenever a neighborhood (bairro) change is detected between address updates.
	 * 
	 * @param {Function|null} callback - Function to call on bairro changes, or null to remove callback
	 * @param {Object} callback.changeDetails - Details about the bairro change
	 * @returns {void}
	 * 
	 * @example
	 * const cache = AddressCache.getInstance();
	 * cache.setBairroChangeCallback((changeDetails) => {
	 *   log('Neighborhood changed:', changeDetails);
	 * });
	 * 
	 * @since 0.9.0-alpha
	 * @author Marcelo Pereira Barbosa
	 */
	setBairroChangeCallback(callback: ((details: unknown) => void) | null) {
		this.callbackRegistry.register('bairro', callback);
		this.bairroChangeCallback = callback;
	}

	/**
	 * Static wrapper for backward compatibility.
	 * @deprecated Use getInstance().setBairroChangeCallback() instead
	 * @static
	 */
	static setBairroChangeCallback(callback: ((details: unknown) => void) | null) {
		return AddressCache.getInstance().setBairroChangeCallback(callback);
	}

	/**
	 * Sets the callback function to be called when municipio changes are detected.
	 * 
	 * This method allows external components to register a callback function that will be
	 * invoked whenever a municipality (municipio) change is detected between address updates.
	 * 
	 * @param {Function|null} callback - Function to call on municipio changes, or null to remove callback
	 * @param {Object} callback.changeDetails - Details about the municipio change
	 * @returns {void}
	 * 
	 * @example
	 * const cache = AddressCache.getInstance();
	 * cache.setMunicipioChangeCallback((changeDetails) => {
	 *   log('Municipality changed:', changeDetails);
	 * });
	 * 
	 * @since 0.9.0-alpha
	 * @author Marcelo Pereira Barbosa
	 */
	setMunicipioChangeCallback(callback: ((details: unknown) => void) | null) {
		this.callbackRegistry.register('municipio', callback);
		this.municipioChangeCallback = callback;
	}

	/**
	 * Static wrapper for backward compatibility.
	 * @deprecated Use getInstance().setMunicipioChangeCallback() instead
	 * @static
	 */
	static setMunicipioChangeCallback(callback: ((details: unknown) => void) | null) {
		return AddressCache.getInstance().setMunicipioChangeCallback(callback);
	}

	/**
	 * Gets the currently registered logradouro change callback.
	 * 
	 * @returns {Function|null} The current callback function or null if none is set
	 * @since 0.9.0-alpha
	 */
	getLogradouroChangeCallback() {
		return this.callbackRegistry.get('logradouro');
	}

	/**
	 * Static wrapper for backward compatibility.
	 * @deprecated Use getInstance().getLogradouroChangeCallback() instead
	 * @static
	 */
	static getLogradouroChangeCallback() {
		return AddressCache.getInstance().getLogradouroChangeCallback();
	}

	/**
	 * Gets the currently registered bairro change callback.
	 * 
	 * @returns {Function|null} The current callback function or null if none is set
	 * @since 0.9.0-alpha
	 */
	getBairroChangeCallback() {
		return this.callbackRegistry.get('bairro');
	}

	/**
	 * Static wrapper for backward compatibility.
	 * @deprecated Use getInstance().getBairroChangeCallback() instead
	 * @static
	 */
	static getBairroChangeCallback() {
		return AddressCache.getInstance().getBairroChangeCallback();
	}

	/**
	 * Gets the currently registered municipio change callback.
	 * 
	 * @returns {Function|null} The current callback function or null if none is set
	 * @since 0.9.0-alpha
	 */
	getMunicipioChangeCallback() {
		return this.callbackRegistry.get('municipio');
	}

	/**
	 * Static wrapper for backward compatibility.
	 * @deprecated Use getInstance().getMunicipioChangeCallback() instead
	 * @static
	 */
	static getMunicipioChangeCallback() {
		return AddressCache.getInstance().getMunicipioChangeCallback();
	}

	/**
	 * Checks if logradouro has changed compared to previous address.
	 * Returns true only once per change to prevent notification loops.
	 * 
	 * **REFACTORED v0.9.0-alpha**: Now delegates to AddressChangeDetector
	 * 
	 * @returns {boolean} True if logradouro has changed and not yet notified
	 * @since 0.9.0-alpha
	 */
	hasLogradouroChanged() {
		const current = this.dataStore.getCurrent().address as Record<string, unknown> | null;
		const previous = this.dataStore.getPrevious().address as Record<string, unknown> | null;
		
		if (!current || !previous) {
			return false;
		}
		
		const hasChanged = this.changeDetector.hasFieldChanged(
			'logradouro',
			current,
			previous
		);
		
		// Sync legacy property for backward compatibility
		if (hasChanged) {
			this.lastNotifiedChangeSignature = 
				`${previous.logradouro}=>${current.logradouro}`;
		}
		
		return hasChanged;
	}

	/**
	 * Static wrapper for backward compatibility.
	 * @deprecated Use getInstance().hasLogradouroChanged() instead
	 * @static
	 */
	static hasLogradouroChanged() {
		return AddressCache.getInstance().hasLogradouroChanged();
	}

	/**
	 * Checks if bairro has changed compared to previous address.
	 * Returns true only once per change to prevent notification loops.
	 * 
	 * **REFACTORED v0.9.0-alpha**: Now delegates to AddressChangeDetector
	 * 
	 * @returns {boolean} True if bairro has changed and not yet notified
	 * @since 0.9.0-alpha
	 */
	hasBairroChanged() {
		const current = this.dataStore.getCurrent().address as Record<string, unknown> | null;
		const previous = this.dataStore.getPrevious().address as Record<string, unknown> | null;
		
		if (!current || !previous) {
			return false;
		}
		
		const hasChanged = this.changeDetector.hasFieldChanged(
			'bairro',
			current,
			previous
		);
		
		// Sync legacy property for backward compatibility
		if (hasChanged) {
			this.lastNotifiedBairroChangeSignature = 
				`${previous.bairro}=>${current.bairro}`;
		}
		
		return hasChanged;
	}

	/**
	 * Static wrapper for backward compatibility.
	 * @deprecated Use getInstance().hasBairroChanged() instead
	 * @static
	 */
	static hasBairroChanged() {
		return AddressCache.getInstance().hasBairroChanged();
	}

	/**
	 * Checks if municipio has changed compared to previous address.
	 * Returns true only once per change to prevent notification loops.
	 * 
	 * **REFACTORED v0.9.0-alpha**: Now delegates to AddressChangeDetector
	 * 
	 * @returns {boolean} True if municipio has changed and not yet notified
	 * @since 0.9.0-alpha
	 */
	hasMunicipioChanged() {
		const current = this.dataStore.getCurrent().address as Record<string, unknown> | null;
		const previous = this.dataStore.getPrevious().address as Record<string, unknown> | null;
		
		if (!current || !previous) {
			return false;
		}
		
		const hasChanged = this.changeDetector.hasFieldChanged(
			'municipio',
			current,
			previous
		);
		
		// Sync legacy property for backward compatibility
		if (hasChanged) {
			this.lastNotifiedMunicipioChangeSignature = 
				`${previous.municipio}=>${current.municipio}`;
		}
		
		return hasChanged;
	}

	/**
	 * Static wrapper for backward compatibility.
	 * @deprecated Use getInstance().hasMunicipioChanged() instead
	 * @static
	 */
	static hasMunicipioChanged() {
		return AddressCache.getInstance().hasMunicipioChanged();
	}

	/**
	 * Gets details about logradouro change.
	 * 
	 * **REFACTORED v0.9.0-alpha**: Now delegates to AddressChangeDetector
	 * 
	 * @returns {Object} Change details with current and previous logradouro
	 * @since 0.9.0-alpha
	 */
	getLogradouroChangeDetails() {
		return this.changeDetector.getChangeDetails(
			'logradouro',
			this.dataStore.getCurrent().address as Record<string, unknown> | null,
			this.dataStore.getPrevious().address as Record<string, unknown> | null
		);
	}

	/**
	 * Static wrapper for backward compatibility.
	 * @deprecated Use getInstance().getLogradouroChangeDetails() instead
	 * @static
	 */
	static getLogradouroChangeDetails() {
		return AddressCache.getInstance().getLogradouroChangeDetails();
	}

	/**
	 * Gets details about bairro change.
	 * 
	 * **REFACTORED v0.9.0-alpha**: Now delegates to AddressChangeDetector
	 * 
	 * @returns {Object} Change details with current and previous bairro
	 * @since 0.9.0-alpha
	 */
	getBairroChangeDetails() {
		const current = this.dataStore.getCurrent().address as Record<string, unknown> | null;
		const previous = this.dataStore.getPrevious().address as Record<string, unknown> | null;
		const currentRawData = this.dataStore.getCurrentRawData();
		const previousRawData = this.dataStore.getPreviousRawData();
		
		// Compute bairroCompleto from raw data if available
		const currentBairroCompleto = this._computeBairroCompleto(currentRawData);
		const previousBairroCompleto = this._computeBairroCompleto(previousRawData);
		
		// Return legacy format for backward compatibility, extended with
		// AddressChangeDetector-compatible fields (to, from, currentAddress, previousAddress)
		// so that ChangeDetectionCoordinator.notifyBairroChangeObservers() can use them.
		return {
			hasChanged: (current?.bairro ?? null) !== (previous?.bairro ?? null),
			current: {
				bairro: current?.bairro,
				bairroCompleto: currentBairroCompleto
			},
			previous: {
				bairro: previous?.bairro,
				bairroCompleto: previousBairroCompleto
			},
			timestamp: Date.now(),
			// AddressChangeDetector-compatible fields
			to: current?.bairro ?? null,
			from: previous?.bairro ?? null,
			currentAddress: current ?? null,
			previousAddress: previous ?? null
		};
	}

	/**
	 * Static wrapper for backward compatibility.
	 * @deprecated Use getInstance().getBairroChangeDetails() instead
	 * @static
	 */
	static getBairroChangeDetails() {
		return AddressCache.getInstance().getBairroChangeDetails();
	}

	/**
	 * Computes complete bairro string from raw address data.
	 * Combines neighbourhood and suburb fields when both are present.
	 * 
	 * @private
	 * @param {Object} rawData - Raw address data from geocoding API
	 * @returns {string} Complete bairro string
	 * @since 0.9.0-alpha
	 */
	_computeBairroCompleto(rawData: unknown): string | null {
		if (!rawData || typeof rawData !== 'object') {
			return null;
		}
		const rd = rawData as { address?: { neighbourhood?: string; suburb?: string; quarter?: string } };
		if (!rd.address) {
			return null;
		}
		const address = rd.address;
		const neighbourhood = address.neighbourhood || null;
		const suburb = address.suburb || null;
		const quarter = address.quarter || null;

		// If we have both neighbourhood and suburb, combine them
		if (neighbourhood && suburb && neighbourhood !== suburb) {
			return `${neighbourhood}, ${suburb}`;
		}

		// Otherwise return whichever is available
		return neighbourhood || suburb || quarter || null;
	}

	/**
	 * Gets details about municipio change.
	 * 
	 * **REFACTORED v0.9.0-alpha**: Now delegates to AddressChangeDetector
	 * 
	 * @returns {Object} Change details with current and previous municipio
	 * @since 0.9.0-alpha
	 */
	getMunicipioChangeDetails() {
		const current = this.dataStore.getCurrent().address as Record<string, unknown> | null;
		const previous = this.dataStore.getPrevious().address as Record<string, unknown> | null;
		
		const currentMunicipio = current?.municipio ?? undefined;
		const previousMunicipio = previous?.municipio ?? undefined;
		const currentUf = current?.uf ?? undefined;
		const previousUf = previous?.uf ?? undefined;
		const currentSiglaUf = current?.siglaUF ?? undefined;
		const previousSiglaUf = previous?.siglaUF ?? undefined;

		return {
			hasChanged: (currentMunicipio ?? null) !== (previousMunicipio ?? null),
			current: {
				municipio: currentMunicipio,
				uf: currentUf,
				siglaUF: currentSiglaUf
			},
			previous: {
				municipio: previousMunicipio,
				uf: previousUf,
				siglaUF: previousSiglaUf
			},
			currentAddress: current ?? null,
			previousAddress: previous ?? null,
			timestamp: Date.now()
		};
	}

	/**
	 * Static wrapper for backward compatibility.
	 * @deprecated Use getInstance().getMunicipioChangeDetails() instead
	 * @static
	 */
	static getMunicipioChangeDetails() {
		return AddressCache.getInstance().getMunicipioChangeDetails();
	}

	/**
	 * Gets a cached or newly extracted Brazilian standard address with change detection.
	 * 
	 * This is the main entry point for retrieving standardized addresses. It coordinates
	 * between cache retrieval, address extraction, and change detection.
	 * 
	 * @param {Object} data - Raw address data from geocoding API
	 * @returns {BrazilianStandardAddress} Standardized address object
	 * @since 0.9.0-alpha
	 */
	getBrazilianStandardAddress(data: unknown) {
		const cacheKey = this.generateCacheKey(data);

		if (cacheKey) {
			// Clean expired entries periodically
			this.cleanExpiredEntries();

			// Check if we have a valid cached entry
			const cached = this.cache.get(cacheKey) as { address: unknown; rawData: unknown } | null;
			if (cached) {
				// LRUCache.get() already handles expiration and LRU updates
				return cached.address;
			}
		}

		// Create new standardized address using AddressExtractor
		const extractor = new AddressExtractor(data as Record<string, unknown>);

		// Cache the result if we have a valid key
		if (cacheKey) {
			// Store in cache (LRUCache handles eviction automatically)
			this.cache.set(cacheKey, {
				address: extractor.enderecoPadronizado,
				rawData: data, // Store raw data for detailed change information
			});

			// Update data store with new address and raw data (v0.9.0-alpha refactoring)
			this.dataStore.update(extractor.enderecoPadronizado, data as NominatimResponse | null);
			
			// Sync legacy properties for backward compatibility
			this.currentAddress = this.dataStore.getCurrent().address;
			this.previousAddress = this.dataStore.getPrevious().address;
			this.currentRawData = this.dataStore.getCurrentRawData();
			this.previousRawData = this.dataStore.getPreviousRawData();

			// Reset change notification flags when new address is cached
			// This allows detection of new changes after cache updates
			this.lastNotifiedChangeSignature = null;
			this.lastNotifiedBairroChangeSignature = null;
			this.lastNotifiedMunicipioChangeSignature = null;

			// NEW (v0.12.12-alpha): Run confirmation buffers before invoking callbacks.
			// Each buffer must see the new value N consecutive times before returning true.
			// This prevents GPS intersection jitter from triggering false address changes.
			const addr = extractor.enderecoPadronizado;

			// Logradouro: delegate entirely to LogradouroChangeTrigger, which owns
			// the announcement decision and the bypassDistance signal.
			const { announce: logradouroReallyChanged } =
				this._logradouroTrigger.observe(addr.logradouro);

			// Bairro / Municipio: keep using AddressFieldConfirmationBuffer directly.
			const bairroWasPreviouslyConfirmed     = this._bairroBuffer.isConfirmed;
			const municipioWasPreviouslyConfirmed  = this._municipioBuffer.isConfirmed;
			const prevConfirmedBairro     = this._bairroBuffer.confirmed;
			const prevConfirmedMunicipio  = this._municipioBuffer.confirmed;

			const bairroConfirmed     = this._bairroBuffer.observe(addr.bairro);
			const municipioConfirmed  = this._municipioBuffer.observe(addr.municipio);

			// A "real" change: buffer confirmed a new value AND there was a prior confirmed
			// value that differs from the new one. Prevents firing on first-ever confirmation.
			const bairroReallyChanged = bairroConfirmed
				&& bairroWasPreviouslyConfirmed
				&& addr.bairro !== prevConfirmedBairro;
			const municipioReallyChanged = municipioConfirmed
				&& municipioWasPreviouslyConfirmed
				&& addr.municipio !== prevConfirmedMunicipio;

			// Notify GeolocationService of pending state so it can switch to fast throttle,
			// and LogradouroChangeTrigger of its own pending state (already handled inside
			// observe() above; bairro/municipio buffers drive the remaining callbacks).
			this._notifyPendingStateChange();

			// Check for logradouro change after caching the new address.
			// logradouroReallyChanged comes from LogradouroChangeTrigger.observe(),
			// which guarantees: buffer confirmed a new value AND it differs from the
			// previously confirmed value.
			if (this.logradouroChangeCallback && logradouroReallyChanged) {
				log("+++ (300) (AddressCache) Detected logradouro change, invoking callback");
				const changeDetails = this.getLogradouroChangeDetails();
				try {
					this.logradouroChangeCallback(changeDetails);
				} catch (callbackError) {
					console.error(
						"(AddressCache) Error calling logradouro change callback:",
						callbackError,
					);
				}
			}

			// Check for bairro change after caching the new address
			if (this.bairroChangeCallback && bairroReallyChanged) {
				const changeDetails = this.getBairroChangeDetails();
				// getBairroChangeDetails() reads dataStore.getPrevious(), which may have
				// been overwritten N-1 times during the confirmation window. Use the
				// buffer's last confirmed value (captured before observe()) instead.
				changeDetails.previous.bairro = prevConfirmedBairro ?? undefined;
				changeDetails.from = prevConfirmedBairro ?? null;
				// Recompute hasChanged against the confirmed previous, not raw dataStore previous
				changeDetails.hasChanged = (changeDetails.current.bairro ?? null) !== (prevConfirmedBairro ?? null);
				try {
					this.bairroChangeCallback(changeDetails);
				} catch (callbackError) {
					console.error(
						"(AddressCache) Error calling bairro change callback:",
						callbackError,
					);
				}
			}

			// Check for municipio change after caching the new address
			if (this.municipioChangeCallback && municipioReallyChanged) {
				const changeDetails = this.getMunicipioChangeDetails();
				// getMunicipioChangeDetails() reads dataStore.getPrevious(), which may
				// have been overwritten N-1 times during the confirmation window. Use
				// the buffer's last confirmed value (captured before observe()) instead.
				changeDetails.previous.municipio = prevConfirmedMunicipio ?? undefined;
				// Recompute hasChanged against the confirmed previous, not raw dataStore previous
				changeDetails.hasChanged = (changeDetails.current.municipio ?? null) !== (prevConfirmedMunicipio ?? null);
				try {
					this.municipioChangeCallback(changeDetails);
				} catch (callbackError) {
					console.error(
						"(AddressCache) Error calling municipio change callback:",
						callbackError,
					);
				}
			}
		}

		this.notifyObservers({ type: 'addressUpdated', address: extractor.enderecoPadronizado, cacheSize: this.getCacheSize() });

		this.notifyFunctions({ type: 'addressUpdated', address: extractor.enderecoPadronizado, cacheSize: this.getCacheSize() });

		// Return the newly extracted standardized address

		return extractor.enderecoPadronizado;
	}

	/**
	 * Static wrapper for backward compatibility.
	 * @deprecated Use getInstance().getBrazilianStandardAddress() instead
	 * @static
	 */
	static getBrazilianStandardAddress(data: unknown) {
		return AddressCache.getInstance().getBrazilianStandardAddress(data);
	}

	toString() {
		return `AddressCache {
			cache: ${this.cache.size},
			currentAddress: ${this.currentAddress},
			previousAddress: ${this.previousAddress}
		}`;
	}

	/**
	 * Static wrapper for backward compatibility.
	 * @deprecated Use getInstance().toString() instead
	 * @static
	 */
	static toString() {
		return AddressCache.getInstance().toString();
	}

	/**
	 * Static wrapper for backward compatibility.
	 * @deprecated Use getInstance().subscribe() instead
	 * @static
	 */
	static subscribe(observer: unknown) {
		return AddressCache.getInstance().subscribe(observer);
	}

	/**
	 * Static wrapper for backward compatibility.
	 * @deprecated Use getInstance().unsubscribe() instead
	 * @static
	 */
	static unsubscribe(observer: unknown) {
		return AddressCache.getInstance().unsubscribe(observer);
	}

	/**
	 * Static wrapper for backward compatibility.
	 * @deprecated Use getInstance().notifyObservers() instead
	 * @static
	 */
	static notifyObservers(event: unknown) {
		return AddressCache.getInstance().notifyObservers(event);
	}

	getCacheSize() {
		return this.cache.size;
	}

	/**
	 * Static wrapper for backward compatibility.
	 * @deprecated Use getInstance().getCacheSize() instead
	 * @static
	 */
	static getCacheSize() {
		return AddressCache.getInstance().getCacheSize();
	}

	subscribeFunction(fn: (...args: unknown[]) => void) {
		return this.observerSubject.subscribeFunction(fn);
	}

	/**
	 * Static wrapper for backward compatibility.
	 * @deprecated Use getInstance().subscribeFunction() instead
	 * @static
	 */
	static subscribeFunction(fn: (...args: unknown[]) => void) {
		return AddressCache.getInstance().subscribeFunction(fn);
	}

	unsubscribeFunction(fn: (...args: unknown[]) => void) {
		return this.observerSubject.unsubscribeFunction(fn);
	}

	/**
	 * Static wrapper for backward compatibility.
	 * @deprecated Use getInstance().unsubscribeFunction() instead
	 * @static
	 */
	static unsubscribeFunction(fn: (...args: unknown[]) => void) {
		return AddressCache.getInstance().unsubscribeFunction(fn);
	}

	notifyFunctions(event: unknown) {
		this.observerSubject.notifyFunctionObservers(event);
	}

	/**
	 * Static wrapper for backward compatibility.
	 * @deprecated Use getInstance().notifyFunctions() instead
	 * @static
	 */
	static notifyFunctions(event: unknown) {
		return AddressCache.getInstance().notifyFunctions(event);
	}

	/**
	 * Static getter for cache property - backward compatibility.
	 * @deprecated Access instance properties through getInstance()
	 * @static
	 */
	static get cache() {
		return AddressCache.getInstance().cache;
	}

	/**
	 * Static setter for cache property - backward compatibility.
	 * @deprecated Access instance properties through getInstance()
	 * @static
	 */
	static set cache(value) {
		AddressCache.getInstance().cache = value;
	}

	/**
	 * Static getter for maxCacheSize property - backward compatibility.
	 * @deprecated Access instance properties through getInstance()
	 * @static
	 */
	static get maxCacheSize() {
		return AddressCache.getInstance().cache.maxSize;
	}

	/**
	 * Static setter for maxCacheSize property - backward compatibility.
	 * @deprecated Access instance properties through getInstance()
	 * @static
	 */
	static set maxCacheSize(value) {
		AddressCache.getInstance().cache.maxSize = value;
	}

	/**
	 * Static getter for cacheExpirationMs property - backward compatibility.
	 * @deprecated Access instance properties through getInstance()
	 * @static
	 */
	static get cacheExpirationMs() {
		return AddressCache.getInstance().cache.expirationMs;
	}

	/**
	 * Static setter for cacheExpirationMs property - backward compatibility.
	 * @deprecated Access instance properties through getInstance()
	 * @static
	 */
	static set cacheExpirationMs(value) {
		AddressCache.getInstance().cache.expirationMs = value;
	}

	/**
	 * Static getter for lastNotifiedChangeSignature property - backward compatibility.
	 * @deprecated Access instance properties through getInstance()
	 * @static
	 */
	static get lastNotifiedChangeSignature() {
		return AddressCache.getInstance().lastNotifiedChangeSignature;
	}

	/**
	 * Static setter for lastNotifiedChangeSignature property - backward compatibility.
	 * @deprecated Access instance properties through getInstance()
	 * @static
	 */
	static set lastNotifiedChangeSignature(value) {
		AddressCache.getInstance().lastNotifiedChangeSignature = value;
	}

	/**
	 * Static getter for lastNotifiedBairroChangeSignature property - backward compatibility.
	 * @deprecated Access instance properties through getInstance()
	 * @static
	 */
	static get lastNotifiedBairroChangeSignature() {
		return AddressCache.getInstance().lastNotifiedBairroChangeSignature;
	}

	/**
	 * Static setter for lastNotifiedBairroChangeSignature property - backward compatibility.
	 * @deprecated Access instance properties through getInstance()
	 * @static
	 */
	static set lastNotifiedBairroChangeSignature(value) {
		AddressCache.getInstance().lastNotifiedBairroChangeSignature = value;
	}

	/**
	 * Static getter for lastNotifiedMunicipioChangeSignature property - backward compatibility.
	 * @deprecated Access instance properties through getInstance()
	 * @static
	 */
	static get lastNotifiedMunicipioChangeSignature() {
		return AddressCache.getInstance().lastNotifiedMunicipioChangeSignature;
	}

	/**
	 * Static setter for lastNotifiedMunicipioChangeSignature property - backward compatibility.
	 * @deprecated Access instance properties through getInstance()
	 * @static
	 */
	static set lastNotifiedMunicipioChangeSignature(value) {
		AddressCache.getInstance().lastNotifiedMunicipioChangeSignature = value;
	}

	/**
	 * Static getter for logradouroChangeCallback property - backward compatibility.
	 * @deprecated Access instance properties through getInstance()
	 * @static
	 */
	static get logradouroChangeCallback() {
		return AddressCache.getInstance().logradouroChangeCallback;
	}

	/**
	 * Static setter for logradouroChangeCallback property - backward compatibility.
	 * @deprecated Access instance properties through getInstance()
	 * @static
	 */
	static set logradouroChangeCallback(value) {
		AddressCache.getInstance().logradouroChangeCallback = value;
	}

	/**
	 * Static getter for bairroChangeCallback property - backward compatibility.
	 * @deprecated Access instance properties through getInstance()
	 * @static
	 */
	static get bairroChangeCallback() {
		return AddressCache.getInstance().bairroChangeCallback;
	}

	/**
	 * Static setter for bairroChangeCallback property - backward compatibility.
	 * @deprecated Access instance properties through getInstance()
	 * @static
	 */
	static set bairroChangeCallback(value) {
		AddressCache.getInstance().bairroChangeCallback = value;
	}

	/**
	 * Static getter for municipioChangeCallback property - backward compatibility.
	 * @deprecated Access instance properties through getInstance()
	 * @static
	 */
	static get municipioChangeCallback() {
		return AddressCache.getInstance().municipioChangeCallback;
	}

	/**
	 * Static setter for municipioChangeCallback property - backward compatibility.
	 * @deprecated Access instance properties through getInstance()
	 * @static
	 */
	static set municipioChangeCallback(value) {
		AddressCache.getInstance().municipioChangeCallback = value;
	}

	/**
	 * Static getter for currentAddress property - backward compatibility.
	 * @deprecated Access instance properties through getInstance()
	 * @static
	 */
	static get currentAddress() {
		return AddressCache.getInstance().currentAddress;
	}

	/**
	 * Static setter for currentAddress property - backward compatibility.
	 * @deprecated Access instance properties through getInstance()
	 * @static
	 */
	static set currentAddress(value) {
		AddressCache.getInstance().currentAddress = value;
	}

	/**
	 * Static getter for previousAddress property - backward compatibility.
	 * @deprecated Access instance properties through getInstance()
	 * @static
	 */
	static get previousAddress() {
		return AddressCache.getInstance().previousAddress;
	}

	/**
	 * Static setter for previousAddress property - backward compatibility.
	 * @deprecated Access instance properties through getInstance()
	 * @static
	 */
	static set previousAddress(value) {
		AddressCache.getInstance().previousAddress = value;
	}

	/**
	 * Static getter for currentRawData property - backward compatibility.
	 * @deprecated Access instance properties through getInstance()
	 * @static
	 */
	static get currentRawData() {
		return AddressCache.getInstance().currentRawData;
	}

	/**
	 * Static setter for currentRawData property - backward compatibility.
	 * @deprecated Access instance properties through getInstance()
	 * @static
	 */
	static set currentRawData(value) {
		AddressCache.getInstance().currentRawData = value;
	}

	/**
	 * Static getter for previousRawData property - backward compatibility.
	 * @deprecated Access instance properties through getInstance()
	 * @static
	 */
	static get previousRawData() {
		return AddressCache.getInstance().previousRawData;
	}

	/**
	 * Static setter for previousRawData property - backward compatibility.
	 * @deprecated Access instance properties through getInstance()
	 * @static
	 */
	static set previousRawData(value) {
		AddressCache.getInstance().previousRawData = value;
	}

	/**
	 * Static getter for observerSubject property - backward compatibility.
	 * @deprecated Access instance properties through getInstance()
	 * @static
	 */
	static get observerSubject() {
		return AddressCache.getInstance().observerSubject;
	}

	/**
	 * Static setter for observerSubject property - backward compatibility.
	 * @deprecated Access instance properties through getInstance()
	 * @static
	 */
	static set observerSubject(value) {
		AddressCache.getInstance().observerSubject = value;
	}

	/**
	 * Destroys the cache and cleans up all resources.
	 * 
	 * Stops the cleanup timer, clears the cache, and releases references.
	 * This method is critical for preventing timer leaks, especially in
	 * test environments where instances are created and destroyed frequently.
	 * 
	 * @returns {void}
	 * @since 0.9.0-alpha
	 * @author Marcelo Pereira Barbosa
	 * 
	 * @example
	 * const cache = AddressCache.getInstance();
	 * // ... use cache
	 * cache.destroy(); // Clean up when done
	 * 
	 * @example
	 * // In tests
	 * afterEach(() => {
	 *   const cache = AddressCache.getInstance();
	 *   cache.destroy();
	 * });
	 */
	destroy() {
		// Stop cleanup timer to prevent leak
		timerManager.clearTimer('address-cache-cleanup');
		
		// Clear all cached data
		this.clearCache();
		
		// Release references to prevent memory leaks
		this.observerSubject = null!;
		this.logradouroChangeCallback = null;
		this.bairroChangeCallback = null;
		this.municipioChangeCallback = null;
		this.currentAddress = null;
		this.previousAddress = null;
		this.currentRawData = null;
		this.previousRawData = null;

		// NEW (v0.12.12-alpha): reset buffers and pending callback on destroy
		this._logradouroTrigger.reset();
		this._bairroBuffer.reset();
		this._municipioBuffer.reset();
		this._pendingConfirmationCallback = null;
		this._anyBufferPending = false;
	}

	/**
	 * Registers a callback that is invoked when any address-field confirmation buffer
	 * transitions into or out of a pending state.
	 *
	 * - Called with `true`  when the first buffer enters a pending state (triggers
	 *   fast-throttle mode in GeolocationService so confirmation reads arrive sooner).
	 * - Called with `false` when all three buffers have settled (confirmed or reset),
	 *   allowing GeolocationService to restore its normal throttle interval.
	 *
	 * Consecutive identical state transitions are suppressed (idempotent).
	 *
	 * @param {(isPending: boolean) => void} cb - Callback receiving the pending state.
	 * @since 0.12.12-alpha
	 */
	setPendingConfirmationCallback(cb: (isPending: boolean) => void): void {
		this._pendingConfirmationCallback = cb;
	}

	/**
	 * Fires the pending-confirmation callback when buffer state changes.
	 * Suppresses consecutive identical calls (FR-03.4).
	 * @private
	 */
	private _notifyPendingStateChange(): void {
		const isPending =
			this._logradouroTrigger.hasPending ||
			this._bairroBuffer.hasPending ||
			this._municipioBuffer.hasPending;

		if (isPending !== this._anyBufferPending) {
			this._anyBufferPending = isPending;
			if (this._pendingConfirmationCallback) {
				try {
					this._pendingConfirmationCallback(isPending);
				} catch (err) {
					console.error('(AddressCache) Error in pendingConfirmationCallback:', err);
				}
			}
		}
	}

	/**
	 * Returns a read-only snapshot of all three confirmation-buffer states.
	 * Intended for diagnostics and debug UI.
	 */
	getConfirmationBufferState(): ConfirmationBufferState {
		return {
			logradouro: {
				confirmed:    this._logradouroTrigger.confirmed,
				isConfirmed:  this._logradouroTrigger.isConfirmed,
				pending:      this._logradouroTrigger.pending,
				hasPending:   this._logradouroTrigger.hasPending,
				pendingCount: this._logradouroTrigger.pendingCount,
				threshold:    this._logradouroTrigger.threshold,
			},
			bairro: {
				confirmed:    this._bairroBuffer.confirmed,
				isConfirmed:  this._bairroBuffer.isConfirmed,
				pending:      this._bairroBuffer.pending,
				hasPending:   this._bairroBuffer.hasPending,
				pendingCount: this._bairroBuffer.pendingConfirmationCount,
				threshold:    this._bairroBuffer.threshold,
			},
			municipio: {
				confirmed:    this._municipioBuffer.confirmed,
				isConfirmed:  this._municipioBuffer.isConfirmed,
				pending:      this._municipioBuffer.pending,
				hasPending:   this._municipioBuffer.hasPending,
				pendingCount: this._municipioBuffer.pendingConfirmationCount,
				threshold:    this._municipioBuffer.threshold,
			},
		};
	}

	/** Static wrapper so callers can use AddressCache.getConfirmationBufferState(). */
	static getConfirmationBufferState(): ConfirmationBufferState {
		return AddressCache.getInstance().getConfirmationBufferState();
	}

	/**
	 * Static wrapper for backward compatibility.
	 * @deprecated Use getInstance().destroy() instead
	 * @static
	 */
	static destroy() {
		return AddressCache.getInstance().destroy();
	}
}

// Apply observer mixin — provides subscribe(), unsubscribe(), and notifyObservers()
// delegating to this.observerSubject, consistent with ReverseGeocoder and PositionManager.
Object.assign(AddressCache.prototype, withObserver());

/**
 * Legacy wrapper class that maintains backward compatibility with existing code.
 * 
 * This class serves as a facade that delegates to the specialized AddressExtractor
 * and AddressCache classes. It preserves the original API surface for existing code
 * while the implementation has been refactored to follow the Single Responsibility Principle.
 * 
 * The refactoring split the original AddressDataExtractor into two classes:
 * - AddressExtractor: Handles address extraction and standardization
 * - AddressCache: Manages caching, change detection, and callbacks
 * 
 * New code should use AddressCache.getBrazilianStandardAddress() directly.
 * This class exists to maintain compatibility with existing tests and consumers.
 * 
 * @class AddressDataExtractor
 * @deprecated Use AddressCache for cache operations and AddressExtractor for extraction
 * @since 0.9.0-alpha
 * @author Marcelo Pereira Barbosa
 */

export default AddressCache;
/**
 * Module exports for address caching.
 * @exports AddressCache - LRU cache for geocoded addresses
 */
export { AddressCache };
